// src/pages/auth/ResetPassword.tsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../../components/common/Button';
import { RootState } from '../../store';
import { changePassword, clearError, clearPasswordChangeMessages } from '../../store/slices/authSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import Icon from '../../components/icons/Icons';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colours.background.default};
`;

const ResetPasswordCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colours.text.secondary};
  text-align: center;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #DDDDDD;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colours.primary};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colours.primary};
  }
`;

const PasswordRequirements = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colours.text.secondary};
  margin-top: 0.25rem;
`;

const SubmitButton = styled(Button)`
  width: 100%;
  background-color: #1A0633;
  color: white;
  
  &:hover {
    background-color: #12042A;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-align: center;
  padding: 0.5rem;
  background-color: #ffebee;
  border-radius: 0.25rem;
  display: ${props => props.children ? 'block' : 'none'};
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-align: center;
  padding: 0.5rem;
  background-color: #e8f5e9;
  border-radius: 0.25rem;
  display: ${props => props.children ? 'block' : 'none'};
`;

interface ResetPasswordFormData {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { isLoading, error, passwordChangeSuccess } = useSelector(
    (state: RootState) => state.auth
  );

  const [token, setToken] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword((prev) => !prev);
  
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(null);

  // Extract token from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      // No token found in URL, redirect to forgot password page
      navigate('/forgot-password');
    }
    
    // Clear any errors
    dispatch(clearError());
    dispatch(clearPasswordChangeMessages());
  }, [dispatch, location, navigate]);

  // Redirect to login page on successful password change
  useEffect(() => {
    if (passwordChangeSuccess) {
      // Wait a moment to show success message before redirecting
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [passwordChangeSuccess, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear password match error when typing in either password field
    if (name === 'newPassword' || name === 'confirmPassword') {
      setPasswordMatchError(null);
    }
  };

  const validatePasswords = (): boolean => {
    if (formData.newPassword !== formData.confirmPassword) {
      setPasswordMatchError('Passwords do not match');
      return false;
    }
    
    // Validate password meets requirements (example: min 8 chars, at least one uppercase, one lowercase, one number, one special char)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.newPassword)) {
      setPasswordMatchError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match and meet requirements
    if (!validatePasswords()) {
      return;
    }
    
    // Dispatch change password action with form data and token
    await dispatch(changePassword({
      changeData: {
        email: formData.email,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      },
      token
    }));
  };

  return (
    <PageContainer>
      <ResetPasswordCard>
        <Title>Reset Password</Title>
        <Subtitle>Please enter your email and new password.</Subtitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {passwordMatchError && <ErrorMessage>{passwordMatchError}</ErrorMessage>}
        {passwordChangeSuccess && (
          <SuccessMessage>
            Password reset successful! Redirecting to login...
          </SuccessMessage>
        )}
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup style={{ position: "relative" }}>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              name="newPassword"
              placeholder="Enter new password"
              value={formData.newPassword}
              onChange={handleChange}
              required
            />
            <span
              onClick={toggleNewPasswordVisibility}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
              }}
            >
              {showNewPassword ? <Icon name='closedEye'/> : <Icon name='openEye'/>}
            </span>
            <PasswordRequirements>
              Password must be at least 8 characters and include uppercase, lowercase, number, and special character
            </PasswordRequirements>
          </FormGroup>

          <FormGroup style={{ position: "relative" }}>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm new password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            <span
              onClick={toggleConfirmPasswordVisibility}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
              }}
            >
              <Icon name={showConfirmPassword ? 'closedEye' : 'openEye'}/>
            </span>
          </FormGroup>
          
          <SubmitButton 
            type="submit" 
            size="large" 
            disabled={isLoading || passwordChangeSuccess}
          >
            {isLoading ? 'Processing...' : 'Reset Password'}
          </SubmitButton>
        </form>
      </ResetPasswordCard>
    </PageContainer>
  );
};

export default ResetPassword;